/**
 * Match events timeline — center-spine layout (matches design): goals show a ball glyph with
 * a gold running-score pill, subs show in (mint ↑) / out (red ↓), cards show a coloured card
 * glyph, VAR shows a muted note. Home events sit left of the spine, away events to the right.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { ArrowDown, ArrowUp, Monitor, Repeat } from 'lucide-react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubEvent } from '../../../src/match/types/matchHub.types';
import { getMatchHubEventsTimelineStyles } from './MatchHubEventsTimeline.styles';

type Styles = ReturnType<typeof getMatchHubEventsTimelineStyles>;

export interface MatchHubEventsTimelineProps {
  events: MatchHubEvent[];
}

function minuteLabel(ev: MatchHubEvent): string {
  if (ev.extraMinute !== undefined) return `${ev.minute}+${ev.extraMinute}`;
  return `${ev.minute}`;
}

const Ball: React.FC = () => (
  <Svg width={18} height={18} viewBox="0 0 24 24">
    <Circle cx={12} cy={12} r={10} fill="#fff" stroke="#111827" strokeWidth={1.4} />
    <Path
      d="M12 6l2.6 1.9-1 3.1h-3.2l-1-3.1L12 6zM6.4 10.2l1 .8-.4 3-2.6.9-1-2.1 3-2.6zm11.2 0l3 2.6-1 2.1-2.6-.9-.4-3 1-.8zM8.6 19.2l-1.3-2.6 1.9-2.5h3.6l1.9 2.5-1.3 2.6H8.6z"
      fill="#111827"
    />
  </Svg>
);

function EventGlyph({ ev, styles, theme }: { ev: MatchHubEvent; styles: Styles; theme: ReturnType<typeof useTheme> }) {
  if (ev.type === 'goal') {
    return (
      <View style={styles.glyphBall}>
        <Ball />
      </View>
    );
  }
  if (ev.type === 'subst') {
    return (
      <View style={styles.glyph}>
        <Repeat size={15} color={theme.gray[500]} />
      </View>
    );
  }
  if (ev.type === 'card') {
    return (
      <View style={styles.glyph}>
        <View style={[styles.cardGlyph, ev.cardKind === 'red' && styles.cardGlyphRed]} />
      </View>
    );
  }
  return (
    <View style={styles.glyph}>
      <Monitor size={15} color={theme.gray[500]} />
    </View>
  );
}

function EventDetail({ ev, styles, theme }: { ev: MatchHubEvent; styles: Styles; theme: ReturnType<typeof useTheme> }) {
  const isHome = ev.side === 'home';
  const mainLine = isHome ? styles.mainLineHome : styles.mainLineAway;

  if (ev.type === 'subst' && (ev.playerOn ?? '').trim() !== '' && (ev.playerOff ?? '').trim() !== '') {
    return (
      <View>
        <View style={isHome ? styles.subLineHome : styles.subLineAway}>
          <Text style={styles.subIn} numberOfLines={1}>{ev.playerOn}</Text>
          <ArrowUp size={13} color={theme.success} strokeWidth={3} />
        </View>
        <View style={isHome ? styles.subLineHome : styles.subLineAway}>
          <Text style={styles.subOut} numberOfLines={1}>{ev.playerOff}</Text>
          <ArrowDown size={13} color={theme.error} strokeWidth={3} />
        </View>
      </View>
    );
  }

  const showScorePill = ev.type === 'goal' && (ev.scoreAfter ?? '').trim() !== '';
  const showCard = ev.type === 'card';
  const isVar = ev.type === 'var';

  return (
    <View>
      <View style={mainLine}>
        {showScorePill ? (
          <View style={styles.scorePill}>
            <Text style={styles.scorePillText}>{ev.scoreAfter}</Text>
          </View>
        ) : null}
        {showCard ? (
          <View style={[styles.cardGlyph, ev.cardKind === 'red' && styles.cardGlyphRed]} />
        ) : null}
        <Text style={[styles.mainText, isVar && styles.mainTextMuted]} numberOfLines={2}>
          {ev.text}
        </Text>
      </View>
      {(ev.assistLabel ?? ev.subtitle ?? '').trim() !== '' ? (
        <Text style={[styles.subText, !isHome && styles.subTextAway]} numberOfLines={1}>
          {ev.assistLabel ?? ev.subtitle}
        </Text>
      ) : null}
    </View>
  );
}

export const MatchHubEventsTimeline: React.FC<MatchHubEventsTimelineProps> = ({ events }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubEventsTimelineStyles(theme), [theme]);

  if (events.length === 0) {
    return <Text style={styles.emptyText}>No events yet.</Text>;
  }

  return (
    <View style={styles.wrap} testID="match-hub-events-timeline">
      <View style={styles.spine} />
      {events.map((ev, index) => {
        const key = `${ev.type}-${ev.minute}-${index}-${ev.text ?? ''}`;

        if (ev.type === 'halftime') {
          return (
            <View key={key} style={styles.divider}>
              <View style={styles.dividerPill}>
                <Text style={styles.dividerLabel}>HT</Text>
                <Text style={styles.dividerScore}>
                  {ev.halftimeScoreHome ?? 0} - {ev.halftimeScoreAway ?? 0}
                </Text>
              </View>
            </View>
          );
        }

        if (ev.type === 'period_info') {
          return (
            <Text key={key} style={styles.periodInfo}>
              {ev.text}
            </Text>
          );
        }

        const isHome = ev.side === 'home';
        const isAway = ev.side === 'away';
        if (!isHome && !isAway) {
          return (
            <Text key={key} style={styles.periodInfo}>
              {ev.text}
            </Text>
          );
        }

        return (
          <View key={key} style={styles.row}>
            <View style={[styles.side, styles.sideHome]}>
              {isHome ? <EventDetail ev={ev} styles={styles} theme={theme} /> : null}
            </View>
            <View style={styles.center}>
              <EventGlyph ev={ev} styles={styles} theme={theme} />
              <Text style={styles.minute}>{minuteLabel(ev)}</Text>
            </View>
            <View style={[styles.side, styles.sideAway]}>
              {isAway ? <EventDetail ev={ev} styles={styles} theme={theme} /> : null}
            </View>
          </View>
        );
      })}
    </View>
  );
};
